import React, { useState, useEffect, useRef, useMemo } from 'react';
import { announcementAPI } from '../../services/api';
import api from '../../services/api';
import { toast } from '../../components/Toast';
import { confirm } from '../../components/ConfirmDialog';
import DjamannLoader from '../../components/DjamannLoader';
import { Volume2, Pencil, Sparkles, Search as SearchIcon, Globe, User, Calendar, PenLine, Eye, X, Check, Inbox, Clock } from 'lucide-react';

const AdminAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    message: '',
    target_user_id: '',
    expires_at: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // États pour l'autocomplétion
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const dropdownRef = useRef(null);
  
  // États pour recherche, filtre et pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive, expired
  const [filterTarget, setFilterTarget] = useState('all'); // all, everyone, specific
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadData();
  }, []);
  
  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Filtrer et paginer les annonces
  const filteredAnnouncements = useMemo(() => {
    let result = [...announcements];
    
    // Recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(a => 
        a.titre?.toLowerCase().includes(query) ||
        a.message?.toLowerCase().includes(query) ||
        a.author_name?.toLowerCase().includes(query) ||
        a.target_user_name?.toLowerCase().includes(query)
      );
    }
    
    // Filtre par statut
    if (filterStatus === 'active') {
      result = result.filter(a => a.is_active && (!a.expires_at || new Date(a.expires_at) > new Date()));
    } else if (filterStatus === 'inactive') {
      result = result.filter(a => !a.is_active);
    } else if (filterStatus === 'expired') {
      result = result.filter(a => a.expires_at && new Date(a.expires_at) <= new Date());
    }
    
    // Filtre par cible
    if (filterTarget === 'everyone') {
      result = result.filter(a => !a.target_user_id);
    } else if (filterTarget === 'specific') {
      result = result.filter(a => a.target_user_id);
    }
    
    return result;
  }, [announcements, searchQuery, filterStatus, filterTarget]);
  
  // Pagination
  const totalPages = Math.ceil(filteredAnnouncements.length / itemsPerPage);
  const paginatedAnnouncements = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAnnouncements.slice(start, start + itemsPerPage);
  }, [filteredAnnouncements, currentPage]);
  
  // Reset page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, filterTarget]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [announcementsRes, usersRes] = await Promise.all([
        announcementAPI.getAll(),
        api.get('/users')
      ]);
      // S'assurer que ce sont des tableaux
      setAnnouncements(Array.isArray(announcementsRes.data) ? announcementsRes.data : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setAnnouncements([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        ...formData,
        target_user_id: formData.target_user_id || null,
        expires_at: formData.expires_at || null
      };

      if (editingId) {
        await announcementAPI.update(editingId, data);
      } else {
        await announcementAPI.create(data);
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ titre: '', message: '', target_user_id: '', expires_at: '' });
      setSelectedUser(null);
      setUserSearch('');
      loadData();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      titre: announcement.titre,
      message: announcement.message,
      target_user_id: announcement.target_user_id || '',
      expires_at: announcement.expires_at ? announcement.expires_at.slice(0, 16) : ''
    });
    // Si un utilisateur cible existe, le sélectionner
    if (announcement.target_user_id) {
      const targetUser = users.find(u => u.id === announcement.target_user_id);
      if (targetUser) {
        setSelectedUser(targetUser);
        setUserSearch(`${targetUser.nom} (${targetUser.email})`);
      }
    } else {
      setSelectedUser(null);
      setUserSearch('');
    }
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm('Supprimer cette annonce ?', { type: 'danger' }))) return;
    
    try {
      await announcementAPI.delete(id);
      loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      await announcementAPI.update(announcement.id, { is_active: !announcement.is_active });
      loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  if (loading) {
    return <DjamannLoader variant="fullscreen" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Volume2 className="w-6 h-6 inline" /> Annonces Administrateur
              </h1>
              <p className="text-white/80 mt-1">
                Envoyez des messages importants à vos utilisateurs
              </p>
            </div>
            <button
              onClick={() => {
                setShowForm(true);
                setEditingId(null);
                setFormData({ titre: '', message: '', target_user_id: '', expires_at: '' });
              }}
              className="px-4 py-2 bg-white text-indigo-600 rounded-xl font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nouvelle annonce
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Formulaire */}
        {showForm && (
          <div className="mb-8 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {editingId ? <><Pencil className="w-5 h-5 inline" /> Modifier l'annonce</> : <><Sparkles className="w-5 h-5 inline" /> Nouvelle annonce</>}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Titre *
                </label>
                <input
                  type="text"
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Ex: Information importante"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Message *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Ex: Jusqu'au mois prochain, les commissions de la plateforme passent à 2.5%"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative" ref={dropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Destinataire
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => {
                        setUserSearch(e.target.value);
                        setShowUserDropdown(true);
                        if (e.target.value === '') {
                          setSelectedUser(null);
                          setFormData({ ...formData, target_user_id: '' });
                        }
                      }}
                      onFocus={() => setShowUserDropdown(true)}
                      className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white pr-10"
                      placeholder="Rechercher un utilisateur ou laisser vide pour tous"
                    />
                    {selectedUser && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUser(null);
                          setUserSearch('');
                          setFormData({ ...formData, target_user_id: '' });
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4 inline" />
                      </button>
                    )}
                  </div>
                  
                  {/* Dropdown des résultats */}
                  {showUserDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {/* Option "Tous les utilisateurs" */}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUser(null);
                          setUserSearch('');
                          setFormData({ ...formData, target_user_id: '' });
                          setShowUserDropdown(false);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-indigo-50 dark:hover:bg-gray-700 flex items-center gap-2 border-b dark:border-gray-600 ${
                          !selectedUser ? 'bg-indigo-50 dark:bg-gray-700' : ''
                        }`}
                      >
                        <span className="text-xl"><Globe className="w-5 h-5 inline" /></span>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Tous les utilisateurs</p>
                          <p className="text-xs text-gray-500">L'annonce sera visible par tout le monde</p>
                        </div>
                      </button>
                      
                      {/* Liste filtrée des utilisateurs */}
                      {users
                        .filter(u => 
                          userSearch === '' ||
                          u.nom?.toLowerCase().includes(userSearch.toLowerCase()) ||
                          u.email?.toLowerCase().includes(userSearch.toLowerCase())
                        )
                        .slice(0, 10)
                        .map(user => (
                          <button
                            type="button"
                            key={user.id}
                            onClick={() => {
                              setSelectedUser(user);
                              setUserSearch(`${user.nom} (${user.email})`);
                              setFormData({ ...formData, target_user_id: user.id });
                              setShowUserDropdown(false);
                            }}
                            className={`w-full px-4 py-2 text-left hover:bg-indigo-50 dark:hover:bg-gray-700 flex items-center gap-3 ${
                              selectedUser?.id === user.id ? 'bg-indigo-50 dark:bg-gray-700' : ''
                            }`}
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                              {user.nom?.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 dark:text-white truncate">{user.nom}</p>
                              <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            </div>
                            <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-600 rounded-full text-gray-600 dark:text-gray-300">
                              {user.role}
                            </span>
                          </button>
                        ))
                      }
                      
                      {users.filter(u => 
                        userSearch === '' ||
                        u.nom?.toLowerCase().includes(userSearch.toLowerCase()) ||
                        u.email?.toLowerCase().includes(userSearch.toLowerCase())
                      ).length === 0 && userSearch !== '' && (
                        <div className="px-4 py-3 text-gray-500 text-center">
                          Aucun utilisateur trouvé
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Indicateur de sélection */}
                  {selectedUser && (
                    <p className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                      <Check className="w-4 h-4 inline" /> Annonce destinée à : {selectedUser.nom}
                    </p>
                  )}
                  {!selectedUser && (
                    <p className="mt-1 text-xs text-gray-500">
                      <Globe className="w-4 h-4 inline" /> Annonce destinée à tous les utilisateurs
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date d'expiration (optionnel)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50"
                >
                  {submitting ? 'Enregistrement...' : (editingId ? 'Mettre à jour' : 'Publier l\'annonce')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Barre de recherche et filtres */}
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1 relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par titre, message, auteur..."
                className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            {/* Filtre par statut */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actives</option>
              <option value="inactive">⏸️ Désactivées</option>
              <option value="expired">⏰ Expirées</option>
            </select>
            
            {/* Filtre par cible */}
            <select
              value={filterTarget}
              onChange={(e) => setFilterTarget(e.target.value)}
              className="px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">Toutes les cibles</option>
              <option value="everyone">Tous les utilisateurs</option>
              <option value="specific">Utilisateur spécifique</option>
            </select>
          </div>
          
          {/* Statistiques de résultats */}
          <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
            <span>
              {filteredAnnouncements.length} annonce{filteredAnnouncements.length > 1 ? 's' : ''} trouvée{filteredAnnouncements.length > 1 ? 's' : ''}
              {(searchQuery || filterStatus !== 'all' || filterTarget !== 'all') && ` sur ${announcements.length}`}
            </span>
            {(searchQuery || filterStatus !== 'all' || filterTarget !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterStatus('all');
                  setFilterTarget('all');
                }}
                className="text-indigo-600 hover:text-indigo-800 font-medium"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        </div>

        {/* Liste des annonces */}
        <div className="space-y-4">
          {filteredAnnouncements.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="text-6xl mb-4">{announcements.length === 0 ? <Inbox className="w-16 h-16 mx-auto text-gray-400" /> : <SearchIcon className="w-16 h-16 mx-auto text-gray-400" />}</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {announcements.length === 0 ? 'Aucune annonce' : 'Aucun résultat'}
              </h3>
              <p className="text-gray-500 mt-2">
                {announcements.length === 0 
                  ? 'Créez votre première annonce pour communiquer avec vos utilisateurs'
                  : 'Essayez de modifier vos critères de recherche'}
              </p>
            </div>
          ) : (
            paginatedAnnouncements.map(announcement => (
              <div 
                key={announcement.id}
                className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden transition-all ${
                  !announcement.is_active ? 'opacity-60' : ''
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                          {announcement.titre}
                        </h3>
                        {!announcement.is_active && (
                          <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                            Désactivée
                          </span>
                        )}
                        {announcement.target_user_id ? (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                            <User className="w-3 h-3 inline" /> {announcement.target_user_name}
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                            <Globe className="w-3 h-3 inline" /> Tous
                          </span>
                        )}
                      </div>
                      
                      <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                        {announcement.message}
                      </p>
                      
                      <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                        <span>
                          <Calendar className="w-4 h-4 inline" /> {new Date(announcement.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        <span>
                          <PenLine className="w-4 h-4 inline" /> {announcement.author_name}
                        </span>
                        <span>
                          <Eye className="w-4 h-4 inline" /> {announcement.read_count || 0} lecture(s)
                        </span>
                        {announcement.expires_at && (
                          <span className={new Date(announcement.expires_at) < new Date() ? 'text-red-500' : ''}>
                            ⏰ Expire: {new Date(announcement.expires_at).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleToggleActive(announcement)}
                        className={`p-2 rounded-lg transition-colors ${
                          announcement.is_active 
                            ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={announcement.is_active ? 'Désactiver' : 'Activer'}
                      >
                        {announcement.is_active ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(announcement)}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                        title="Modifier"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                        title="Supprimer"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Première page"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Page précédente"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Numéros de page */}
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  // Afficher: première, dernière, courante, et 1 de chaque côté de la courante
                  return page === 1 || 
                         page === totalPages || 
                         Math.abs(page - currentPage) <= 1;
                })
                .map((page, index, arr) => (
                  <React.Fragment key={page}>
                    {/* Ajouter ... si écart > 1 avec le précédent */}
                    {index > 0 && page - arr[index - 1] > 1 && (
                      <span className="px-2 text-gray-400">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-indigo-600 text-white'
                          : 'border dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))
              }
            </div>
            
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Page suivante"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Dernière page"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnnouncements;
